import { api } from '../services/api';
import {
  LabelTemplate,
  CreateLabelTemplateDto,
  UpdateLabelTemplateDto,
  PrintLabelDto,
  PrintBatchLabelsDto,
  PrintLabelResponse,
  PreviewLabelResponse,
  LabelTemplateType,
  PriceChangesResponse,
} from '../types/label-templates.types';

export const labelTemplatesApi = {
  /**
   * Créer un nouveau template
   */
  async create(data: CreateLabelTemplateDto): Promise<LabelTemplate> {
    return api.post<LabelTemplate>('/label-templates', data);
  },

  /**
   * Lister tous les templates
   */
  async findAll(type?: LabelTemplateType): Promise<LabelTemplate[]> {
    const params = type ? { type } : {};
    return api.get<LabelTemplate[]>('/label-templates', { params });
  },

  /**
   * Récupérer le template par défaut pour un type
   */
  async getDefault(type: LabelTemplateType): Promise<LabelTemplate> {
    return api.get<LabelTemplate>(`/label-templates/default/${type}`);
  },

  /**
   * Récupérer un template par ID
   */
  async findOne(id: string): Promise<LabelTemplate> {
    return api.get<LabelTemplate>(`/label-templates/${id}`);
  },

  /**
   * Mettre à jour un template
   */
  async update(id: string, data: UpdateLabelTemplateDto): Promise<LabelTemplate> {
    return api.patch<LabelTemplate>(`/label-templates/${id}`, data);
  },

  /**
   * Supprimer un template
   */
  async delete(id: string): Promise<{ message: string }> {
    return api.delete<{ message: string }>(`/label-templates/${id}`);
  },

  /**
   * Définir un template comme par défaut
   */
  async setAsDefault(id: string): Promise<LabelTemplate> {
    return api.post<LabelTemplate>(`/label-templates/${id}/set-default`);
  },

  /**
   * Dupliquer un template
   */
  async duplicate(id: string, newName: string): Promise<LabelTemplate> {
    return api.post<LabelTemplate>(
      `/label-templates/${id}/duplicate`,
      null,
      { params: { name: newName } }
    );
  },

  /**
   * Imprimer une étiquette
   */
  async print(data: PrintLabelDto): Promise<PrintLabelResponse> {
    return api.post<PrintLabelResponse>('/label-templates/print', data);
  },

  /**
   * Imprimer plusieurs étiquettes
   */
  async printBatch(data: PrintBatchLabelsDto): Promise<PrintLabelResponse> {
    return api.post<PrintLabelResponse>('/label-templates/print/batch', data);
  },

  /**
   * Prévisualiser le ZPL sans imprimer
   */
  async preview(data: PrintLabelDto): Promise<PreviewLabelResponse> {
    return api.post<PreviewLabelResponse>('/label-templates/preview', data);
  },

  /**
   * Seed les templates par défaut
   */
  async seed(): Promise<{ success: boolean; message: string }> {
    return api.post<{ success: boolean; message: string }>('/label-templates/seed');
  },

  /**
   * Détecter les changements de prix depuis la dernière impression
   */
  /**
   * Les étiquettes d'un ou plusieurs rayons, prix inchangés compris
   * (`include: 'all'`) : c'est la tournée hebdomadaire, où l'on réimprime tout
   * parce que les cageots bougent, pas parce que les prix ont changé.
   */
  async getLabelsToPrint(
    rayons: string[],
    toutes: boolean,
    enStock = true,
  ): Promise<PriceChangesResponse> {
    const q = new URLSearchParams();
    if (rayons.length) q.set('rayons', rayons.join(','));
    if (toutes) q.set('include', 'all');
    if (enStock) q.set('en_stock', '1');
    return api.get<PriceChangesResponse>(`/label-templates/price-changes?${q.toString()}`);
  },

  /** Le ZPL de N étiquettes en UN appel (233 allers-retours, sinon). */
  async previewBatch(labels: Array<{ productId: string; templateId: string }>) {
    return api.post<{
      total: number;
      generated: number;
      items: Array<{ product_id: string; zpl?: string; error?: string }>;
      zpl: string;
    }>('/label-templates/preview-batch', { labels });
  },

  /**
   * Déclarer ce que le bridge local vient d'imprimer : sans ça le journal reste
   * vide et « déjà imprimé » ne veut rien dire.
   */
  async logPrinted(labels: Array<{ productId: string; templateId: string; quantity?: number }>) {
    return api.post<{ enregistrees: number; demandees: number }>(
      '/label-templates/print-log',
      { labels },
    );
  },

  async getPriceChanges(): Promise<PriceChangesResponse> {
    return api.get<PriceChangesResponse>('/label-templates/price-changes');
  },

  /**
   * Imprimer une étiquette texte libre
   */
  async printFreetext(data: {
    text: string;
    format: 'SHELF' | 'MEDIUM' | 'BULK';
    fontSize?: number;
    align?: 'left' | 'center' | 'right';
    bold?: boolean;
    quantity?: number;
  }): Promise<PrintLabelResponse & { preview_url: string }> {
    return api.post<PrintLabelResponse & { preview_url: string }>('/label-templates/print-freetext', data);
  },
};
